import { z } from 'zod'
import { api } from '@/shared/api'

export const authUserSchema = z.object({
  id: z.string(),
  bubbleId: z.string().nullable().optional(),
  email: z.string().email(),
  name: z.string().nullable().optional(),
  telegram: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  position: z.string().nullable().optional(),
  acceptedUserAgreement: z.boolean().optional(),
  profileComplete: z.boolean().optional(),
  missingProfileFields: z.array(z.enum(['name', 'telegram', 'company', 'position'])).optional(),
  userCarrotQuestToken: z.string().nullable().optional(),
  role: z.string().optional(),
  systemRole: z.string().optional(),
  systemRoles: z.array(z.string()).optional(),
  subscriptionStatus: z.string().nullable().optional(),
  subscriptionStatusKnown: z.boolean().optional(),
  subscriptionActive: z.boolean().optional(),
  accessVersion: z.string().nullable().optional(),
})

export const authResponseSchema = z.object({
  success: z.boolean(),
  isNewUser: z.boolean().optional(),
  profileComplete: z.boolean().optional(),
  missingProfileFields: z.array(z.enum(['name', 'telegram', 'company', 'position'])).optional(),
  nextStep: z.enum(['redirect', 'profile']).optional(),
  redirectBranch: z.enum(['active', 'inactive']).optional(),
  redirectPath: z.string().nullable().optional(),
  defaultPriceUrl: z.string().nullable().optional(),
  user: authUserSchema,
})

export const authMeSchema = z.object({
  user: authUserSchema,
})

export const authConsentStateSchema = z.object({
  email: z.string().email(),
  acceptedUserAgreement: z.boolean(),
  requiresAgreement: z.boolean(),
})

export const authRequestSuccessSchema = z.object({
  success: z.boolean(),
})

export type AuthUser = z.infer<typeof authUserSchema>
export type AuthResponse = z.infer<typeof authResponseSchema>
export type AuthMeResponse = z.infer<typeof authMeSchema>
export type AuthConsentState = z.infer<typeof authConsentStateSchema>
export type AuthRequestSuccessResponse = z.infer<typeof authRequestSuccessSchema>

export async function login(credentials: {
  email: string
  password: string
  acceptedUserAgreement?: boolean
}) {
  const response = await api.post<AuthResponse>('/api/v1/auth/login', credentials)
  return authResponseSchema.parse(response)
}

export async function register(credentials: { email: string; password: string }) {
  const response = await api.post<AuthResponse>('/api/v1/auth/register', credentials)
  return authResponseSchema.parse(response)
}

export async function logout() {
  await api.post('/api/v1/auth/logout', {})
}

export async function me() {
  const response = await api.get<AuthMeResponse>('/api/v1/auth/me')
  return authMeSchema.parse(response)
}

export async function getConsentState(email: string) {
  const response = await api.get<AuthConsentState>('/api/v1/auth/consent', {
    params: { email },
  })
  return authConsentStateSchema.parse(response)
}

export async function updateProfile(input: {
  name?: string
  telegram?: string
  company?: string
  position?: string
  acceptedUserAgreement?: boolean
}) {
  const response = await api.post<AuthResponse>('/api/v1/auth/profile', input)
  return authResponseSchema.parse(response)
}

export async function requestPasswordReset(payload: { email: string }) {
  const response = await api.post<AuthRequestSuccessResponse>(
    '/api/v1/auth/password-reset/request',
    payload
  )
  return authRequestSuccessSchema.parse(response)
}

export async function confirmPasswordReset(payload: { token: string; password: string }) {
  const response = await api.post<AuthResponse>('/api/v1/auth/password-reset/confirm', payload)
  return authResponseSchema.parse(response)
}

export async function requestMagicLink(payload: { email: string }) {
  const response = await api.post<AuthRequestSuccessResponse>(
    '/api/v1/auth/magic-link/request',
    payload
  )
  return authRequestSuccessSchema.parse(response)
}

export async function consumeMagicLink(token: string) {
  const response = await api.get<AuthResponse>('/api/v1/auth/magic-link/consume', {
    params: { token },
  })
  return authResponseSchema.parse(response)
}
